import { z } from 'zod'
import { MAX_BANK_DISCOUNT_MILLI } from '../helpers/sales-money.js'
import { salesRatesSchema } from './sales-price-list.dto.js'
import { salesLineGroupSchema, salesPacketKindSchema, salesPacketStatusSchema } from '../enums/sales-packet.enum.js'
import { scheduleDueSchema, scheduleEntrySchema } from '../helpers/sales-schedule.js'
import { calendarDaySchema, instantSchema, wallClockSchema } from '../validators/temporal.validator.js'
import { autopayDtoSchema, bookingPaymentDtoSchema } from './sales-payment.dto.js'

/**
 * An event's proposals as its couple and planner see them (docs/sales.spec.md,
 * "Choosing and signing"): what's offered, what was picked, and what it comes
 * to. Never the price list.
 */

export const bookingLineDtoSchema = z.object({
  id: z.string().uuid(),
  group: salesLineGroupSchema,
  name: z.string(),
  description: z.string(),
  unitCents: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  taxable: z.boolean(),
  serviceCharged: z.boolean(),
  /** A package or add-on the couple picked. An included line always is. */
  chosen: z.boolean(),
})
export type BookingLineDto = z.infer<typeof bookingLineDtoSchema>

export const bookingPacketDtoSchema = z.object({
  id: z.string().uuid(),
  kind: salesPacketKindSchema,
  status: salesPacketStatusSchema,
  date: calendarDaySchema,
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  validUntil: calendarDaySchema,
  /** Past its last day at the venue: nothing more can be chosen, signed or paid. */
  lapsed: z.boolean(),
  lines: z.array(bookingLineDtoSchema),
  /** The rates it was sent with, so the total can be worked out as the couple picks. */
  rates: salesRatesSchema,
  bankDiscountMilli: z.number().int().min(0).max(MAX_BANK_DISCOUNT_MILLI),
  schedule: z.array(scheduleEntrySchema),
  securityDeposit: z.object({ cents: z.number().int(), due: scheduleDueSchema }).nullable(),
  signers: z.array(z.object({ name: z.string(), signed: z.boolean() })),
  /** This person signs it, it's still out, and nobody has signed yet. False for a planner. */
  canChoose: z.boolean(),
  /** This person signs it, hasn't yet, it's still out, and a package is chosen if there are any. */
  canSign: z.boolean(),
  closedAt: instantSchema.nullable(),
  /** A change still out: what's been paid toward the agreement, which its schedule comes after. */
  paidBeforeCents: z.number().int().nullable().default(null),
})
export type BookingPacketDto = z.infer<typeof bookingPacketDtoSchema>

export const bookingDtoSchema = z.object({
  eventId: z.string().uuid(),
  eventName: z.string(),
  venueName: z.string(),
  /** Newest first. */
  packets: z.array(bookingPacketDtoSchema),
  /** What's owed under what they signed, in order. Defaulted while an older API answers. */
  payments: z.array(bookingPaymentDtoSchema).default([]),
  /** Their saved method, while autopay is on; null for a planner. */
  autopay: autopayDtoSchema.nullable().default(null),
})
export type BookingDto = z.infer<typeof bookingDtoSchema>

/** Where to sign: Pixel Sign's page, shown inside the app. Opened once, and soon. */
export const signingLinkDtoSchema = z.object({ embedUrl: z.string().url() })
export type SigningLinkDto = z.infer<typeof signingLinkDtoSchema>
